
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

const RenewableEnergyPage = () => {
  useEffect(() => {
    // SEO設定
    document.title = '再生可能エネルギー事業 | 株式会社伊藤総業 - 太陽光発電システム';
    
    // Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "再生可能エネルギー事業 - 株式会社伊藤総業",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/renewable-energy`,
      "description": "株式会社伊藤総業の再生可能エネルギー事業。太陽光発電システムの企画・開発・販売により持続可能なエネルギーソリューションを提供。",
      "mainEntity": {
        "@type": "Service",
        "name": "再生可能エネルギー事業",
        "provider": {
          "@type": "Organization",
          "name": "株式会社伊藤総業"
        },
        "serviceType": "太陽光発電システム",
        "areaServed": {
          "@type": "Place",
          "name": "東京都世田谷区"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
      <Header />
      
      {/* Hero Section */}
      <main>
        <section 
          className="relative h-96 flex items-center justify-center bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/5bfde9a2f539a98ee61c44fdf806a070.jpg')`
          }}
        >
          <div className="container mx-auto px-4 text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              再生可能エネルギー事業
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              持続可能な未来のために<br />
              クリーンエネルギーソリューションを提供
            </p>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">事業内容</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                再生可能エネルギーの企画から開発、販売まで一貫したサービスを提供
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-sun-line text-yellow-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">太陽光発電システム</h3>
                <p className="text-gray-600">
                  住宅用から産業用まで、最適な太陽光発電システムの企画・設計・施工を行います。
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-leaf-line text-green-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">エネルギー効率化</h3>
                <p className="text-gray-600">
                  省エネルギー技術の導入により、コスト削減と環境負荷軽減を実現します。
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-battery-charge-line text-blue-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">蓄電システム</h3>
                <p className="text-gray-600">
                  エネルギーの安定供給を実現する蓄電システムの企画・開発・運営を行います。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Battery Storage Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="h-96 bg-cover bg-center rounded-xl" style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Large%20scale%20battery%20energy%20storage%20facility%2C%20industrial%20battery%20storage%20system%2C%20modern%20energy%20storage%20infrastructure%2C%20clean%20technology%20installation%2C%20professional%20energy%20storage%20business&width=600&height=400&seq=battery-storage&orientation=landscape')`
              }}></div>
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">蓄電所事業</h2>
                <p className="text-lg text-gray-600 mb-6">
                  大規模蓄電所の企画から設計、運営まで一貫して手がけ、エネルギーの安定供給と効率的な電力管理を実現します。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <i className="ri-check-line text-blue-600"></i>
                    </div>
                    <span className="text-gray-700">大容量蓄電システムの設計・施工</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <i className="ri-check-line text-blue-600"></i>
                    </div>
                    <span className="text-gray-700">エネルギー管理システムの構築</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <i className="ri-check-line text-blue-600"></i>
                    </div>
                    <span className="text-gray-700">24時間監視・運営サポート</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">導入メリット</h2>
              <p className="text-xl text-gray-600">
                再生可能エネルギーがもたらす様々な効果
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-money-dollar-circle-line text-green-600 text-3xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">コスト削減</h3>
                <p className="text-gray-600">
                  電気料金の大幅な削減を実現
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-earth-line text-blue-600 text-3xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">環境貢献</h3>
                <p className="text-gray-600">
                  CO2削減で地球環境に貢献
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-purple-600 text-3xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">エネルギー自立</h3>
                <p className="text-gray-600">
                  安定したエネルギー供給を確保
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-trophy-line text-orange-600 text-3xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">企業価値向上</h3>
                <p className="text-gray-600">
                  ESG経営で企業価値を向上
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">導入の流れ</h2>
              <p className="text-xl text-gray-600">
                お客様のニーズに合わせた最適なソリューションをご提案
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  1
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">現地調査</h3>
                <p className="text-gray-600">
                  設置場所の詳細な調査を実施
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  2
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">設計・提案</h3>
                <p className="text-gray-600">
                  最適なシステム設計をご提案
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  3
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">契約・許可</h3>
                <p className="text-gray-600">
                  各種手続きと許可申請を代行
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  4
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">施工・設置</h3>
                <p className="text-gray-600">
                  専門技術者による安全な施工
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  5
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">運用開始</h3>
                <p className="text-gray-600">
                  運用開始とアフターサポート
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-green-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              持続可能なエネルギーソリューションをご提案
            </h2>
            <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
              再生可能エネルギーの導入をお考えの方は、お気軽にご相談ください
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact" 
                className="bg-white text-green-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                お問い合わせフォーム
              </Link>
              <a 
                href="tel:070-4228-3984" 
                className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-green-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                070-4228-3984
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default RenewableEnergyPage;
